import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { ExtensionSelect, type PbxExtensionOption } from "@/components/ExtensionSelect";

type UserRow = {
  id: number | string;
  name: string;
  mobile: string;
  extension: string | null;
  role: string;
  status: string;
  phoneMode?: "desk" | "sip";
  sipPasswordSet?: boolean;
};

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function UsersPage() {
  const { user, refresh } = useSession();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [pbxExts, setPbxExts] = useState<PbxExtensionOption[]>([]);
  const [extsReady, setExtsReady] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [addExt, setAddExt] = useState("");
  const [editExt, setEditExt] = useState("");

  async function reload() {
    const [list, exts] = await Promise.all([
      api<UserRow[]>("/users"),
      api<PbxExtensionOption[]>("/org/extensions").catch(() => [] as PbxExtensionOption[]),
    ]);
    setRows(Array.isArray(list) ? list : []);
    setPbxExts(Array.isArray(exts) ? exts : []);
    setExtsReady(true);
  }

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Could not load users"));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.mobile || "").includes(q) ||
        (u.extension || "").includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [rows, query]);

  function openAdd() {
    setAddExt("");
    setError("");
    setAddOpen(true);
  }

  function openEdit(u: UserRow) {
    setEditExt(u.extension || "");
    setError("");
    setEditing(u);
  }

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(e.currentTarget);
    try {
      await api("/users", {
        method: "POST",
        body: JSON.stringify({
          name: form.get("name"),
          mobile: form.get("mobile"),
          role: form.get("role"),
          extension: addExt || form.get("extension") || "",
        }),
      });
      setAddOpen(false);
      await reload();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add user");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    const form = new FormData(e.currentTarget);
    try {
      await api(`/users/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: form.get("name"),
          mobile: form.get("mobile"),
          role: form.get("role"),
          extension: editExt,
          status: form.get("status"),
          phoneMode: form.get("phoneMode") || "desk",
          sipPassword: String(form.get("sipPassword") || "") || undefined,
        }),
      });
      setEditing(null);
      await reload();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update user");
    } finally {
      setBusy(false);
    }
  }

  async function remove(u: UserRow) {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    setError("");
    try {
      await api(`/users/${u.id}`, { method: "DELETE" });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete");
    }
  }

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const modalOpen = addOpen || Boolean(editing);

  return (
    <div className="users-page">
      <div className="ext-page-head">
        <div>
          <h1>Users</h1>
          <p className="muted">
            Map each login to a PBX desk · Softphone needs SIP password ·{" "}
            <Link to="/app/extensions">Extensions</Link>
            {" · "}
            <Link to="/app/softphone">Softphone</Link>
          </p>
        </div>
        {isAdmin && (
          <button className="btn" type="button" onClick={openAdd}>
            Add user
          </button>
        )}
      </div>

      {error && !modalOpen ? <p className="error">{error}</p> : null}

      <section className="panel ext-panel">
        <div className="users-toolbar">
          <input
            className="search"
            placeholder="Search name, mobile, extension…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <table className="table ext-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Mobile</th>
              <th>Extension</th>
              <th>Role</th>
              <th>Status</th>
              {isAdmin ? <th className="ext-col-actions">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 6 : 5} className="muted">
                  No users
                </td>
              </tr>
            )}
            {visible.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="who-row" style={{ gap: 10 }}>
                    <div className="avatar">{initials(u.name)}</div>
                    <b>{u.name}</b>
                  </div>
                </td>
                <td>{u.mobile}</td>
                <td className="ext-read">{u.extension || "—"}</td>
                <td>
                  <span className="pill">{u.role}</span>
                </td>
                <td>
                  <span className={`pill ${u.status === "active" ? "live" : "dead"}`}>
                    {u.status}
                  </span>
                </td>
                {isAdmin ? (
                  <td className="ext-col-actions">
                    <div className="row-actions">
                      <button className="btn ghost btn-tiny" type="button" onClick={() => openEdit(u)}>
                        Edit
                      </button>
                      <button className="btn ghost btn-tiny" type="button" onClick={() => void remove(u)}>
                        Delete
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {addOpen && (
        <div className="modal-back" onClick={() => setAddOpen(false)}>
          <div className="modal user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Add user</h2>
                <p className="muted">Create a login and map it to a PBX extension.</p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setAddOpen(false)}
              >
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={(e) => void create(e)}>
              <div className="modal-body">
                <label className="field">
                  <span>Name</span>
                  <input name="name" required autoFocus />
                </label>
                <label className="field">
                  <span>Mobile</span>
                  <input name="mobile" required inputMode="tel" placeholder="10-digit mobile" />
                </label>
                <label className="field">
                  <span>PBX extension</span>
                  <ExtensionSelect
                    name="extension"
                    value={addExt}
                    onChange={setAddExt}
                    extensions={pbxExts}
                    loading={!extsReady}
                    allowEmpty
                    emptyLabel="Not mapped"
                    exclusive
                  />
                  <span className="field-hint">
                    {extsReady && !pbxExts.length
                      ? "No PBX extensions yet — sync under Extensions first."
                      : "One desk per user. Taken extensions are disabled."}
                  </span>
                </label>
                <label className="field">
                  <span>Role</span>
                  <select name="role" defaultValue="agent">
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                {error ? <p className="error">{error}</p> : null}
              </div>
              <div className="btn-row">
                <button className="btn" disabled={busy}>
                  {busy ? "Saving…" : "Create"}
                </button>
                <button type="button" className="btn ghost" onClick={() => setAddOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-back" onClick={() => setEditing(null)}>
          <div className="modal user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Edit user</h2>
                <p className="muted">
                  {editing.name} · map to a PBX extension for click-to-call
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setEditing(null)}
              >
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={(e) => void saveEdit(e)}>
              <div className="modal-body">
                <label className="field">
                  <span>Name</span>
                  <input name="name" defaultValue={editing.name} required autoFocus />
                </label>
                <label className="field">
                  <span>Mobile</span>
                  <input name="mobile" defaultValue={editing.mobile} required inputMode="tel" />
                </label>
                <label className="field">
                  <span>PBX extension</span>
                  <ExtensionSelect
                    name="extension"
                    value={editExt}
                    onChange={setEditExt}
                    extensions={pbxExts}
                    loading={!extsReady}
                    allowEmpty
                    emptyLabel="Not mapped"
                    currentUserId={String(editing.id)}
                    exclusive
                  />
                  <span className="field-hint">
                    {extsReady && !pbxExts.length
                      ? "No PBX extensions yet — open Extensions and Sync from PBX."
                      : "One extension can belong to one user only. Taken desks are disabled."}
                  </span>
                </label>
                <div className="form-grid">
                  <label className="field">
                    <span>Role</span>
                    <select name="role" defaultValue={editing.role === "admin" || editing.role === "owner" ? "admin" : "agent"}>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                  <label className="field">
                    <span>Status</span>
                    <select name="status" defaultValue={editing.status || "active"}>
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                </div>
                <label className="field">
                  <span>Phone mode</span>
                  <select name="phoneMode" defaultValue={editing.phoneMode || "desk"}>
                    <option value="desk">Desk / hard phone</option>
                    <option value="sip">Softphone (microphone)</option>
                  </select>
                </label>
                <label className="field">
                  <span>SIP password</span>
                  <input
                    name="sipPassword"
                    type="password"
                    placeholder={
                      editing.sipPasswordSet ? "•••••••• (leave blank to keep)" : "Neron extension password"
                    }
                    autoComplete="new-password"
                  />
                  <span className="field-hint">
                    Required for browser softphone — same password as the extension on the PBX.
                  </span>
                </label>
                {error ? <p className="error">{error}</p> : null}
              </div>
              <div className="btn-row">
                <button className="btn" disabled={busy}>
                  {busy ? "Saving…" : "Save"}
                </button>
                <button type="button" className="btn ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
